import { useRef, useState } from 'react';
import { useActionLock } from '@breaker/action-lock';
import { api } from '@/auth/api';
import { useMe } from '@/data/me';
import { useActiveFleet } from '@/data/fleets';
import { useFleetTickets, useInvalidateSupport, type SupportTicketStatus } from '@/data/support';
import { Button } from '@/components/ui/button';

/**
 * /support — fleet users raise a ticket (same low-threshold rail as the
 * help-menu dialog) + a READ-ONLY list of their fleet's tickets with reply
 * threads. The platform owner's global queue moved to the admin console
 * (Phase A U4, law-4 migration).
 */

const STATUS_LABEL: Record<SupportTicketStatus, string> = {
  open: 'Open',
  replied: 'Replied',
  closed: 'Closed',
};

function StatusChip({ status }: { status: SupportTicketStatus }) {
  const tone =
    status === 'open'
      ? 'bg-breaker-red/10 text-breaker-red-deep border-breaker-red/30'
      : status === 'replied'
        ? 'bg-breaker-ground-2 text-breaker-ink-2 border-breaker-line-2'
        : 'bg-breaker-ground-2 text-breaker-mute border-breaker-line';
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-2xs uppercase tracking-wider ${tone}`}>
      {STATUS_LABEL[status]}
    </span>
  );
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------- fleet view

function RaiseTicket({ fleetId, onRaised }: { fleetId: string | null; onRaised: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sendLock = useActionLock();

  async function send() {
    setError(null);
    try {
      let screenshotKey: string | null = null;
      if (file) {
        const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const init = await api<{
          key: string;
          uploadUrl: string;
          uploadHeaders: Record<string, string>;
        }>('/v1/support-reports/screenshot-init', {
          method: 'POST',
          body: { mimeType, sizeBytes: file.size },
        });
        const put = await fetch(init.uploadUrl, {
          method: 'PUT',
          headers: init.uploadHeaders,
          body: file,
        });
        if (put.ok) screenshotKey = init.key;
      }
      await api('/v1/support-reports', {
        method: 'POST',
        body: {
          message: message.trim(),
          app: 'dispatcher',
          fleetId,
          appVersion: __APP_VERSION__,
          build: __BUILD_TIME__,
          screen: '/support',
          deviceOs: navigator.userAgent.slice(0, 80),
          screenshotKey,
        },
      });
      setSent(true);
      setMessage('');
      setFile(null);
      onRaised();
    } catch {
      setError("Couldn't send it. Try again.");
    }
  }

  if (sent) {
    return (
      <div className="rounded-lg border border-breaker-line bg-white p-4 space-y-3">
        <p className="text-sm text-breaker-ink">Sent — Breaker support will reply by email.</p>
        <Button size="sm" onClick={() => setSent(false)}>
          Raise another
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-breaker-line bg-white p-4 space-y-3">
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={4}
        maxLength={4000}
        placeholder="Tell us what's happening — nothing's too small to mention."
        className="w-full rounded-md border border-breaker-line bg-white px-3 py-2 text-sm text-breaker-ink placeholder:text-breaker-mute focus:outline-none focus:ring-2 focus:ring-breaker-line-2"
      />
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-sm">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {file ? (
            <>
              <span className="text-breaker-ink-2">{file.name}</span>
              <button
                type="button"
                className="text-breaker-mute underline underline-offset-2"
                onClick={() => {
                  setFile(null);
                  if (fileRef.current) fileRef.current.value = '';
                }}
              >
                Remove
              </button>
            </>
          ) : (
            <button
              type="button"
              className="text-breaker-ink-2 hover:text-breaker-ink"
              onClick={() => fileRef.current?.click()}
            >
              + Add a screenshot (optional)
            </button>
          )}
        </div>
        <Button
          size="sm"
          disabled={message.trim().length === 0}
          loading={sendLock.locked}
          onClick={() => sendLock.run(send)}
        >
          Send
        </Button>
      </div>
      {error ? <p className="text-sm text-breaker-red-deep">{error}</p> : null}
    </div>
  );
}

function FleetSupport() {
  const { fleet } = useActiveFleet();
  const me = useMe();
  const tickets = useFleetTickets(fleet?.id ?? null);
  const invalidate = useInvalidateSupport();

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-breaker-ink">Support</h1>
        <p className="text-sm text-breaker-mute mt-1">
          Report a problem or ask a question — we reply by email.
        </p>
      </div>

      <RaiseTicket
        fleetId={fleet?.id ?? null}
        onRaised={() => {
          if (fleet?.id) void invalidate.fleet(fleet.id);
        }}
      />

      <div className="space-y-2">
        <h2 className="text-sm font-medium text-breaker-ink-2 uppercase tracking-wider">
          Your fleet's tickets
        </h2>
        {tickets.isLoading ? (
          <p className="text-sm text-breaker-mute">Loading…</p>
        ) : (tickets.data ?? []).length === 0 ? (
          <p className="text-sm text-breaker-mute">Nothing raised yet.</p>
        ) : (
          <ul className="space-y-2">
            {tickets.data!.map((t) => (
              <li key={t.id} className="rounded-lg border border-breaker-line bg-white p-4 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-breaker-mute">
                    {when(t.createdAt)} · {t.app}
                    {t.userId === me.data?.id ? ' · you' : ''}
                  </span>
                  <StatusChip status={t.status} />
                </div>
                <p className="text-sm text-breaker-ink whitespace-pre-wrap">{t.message}</p>
                {t.messages.length > 0 ? (
                  <div className="border-t border-breaker-line pt-2 space-y-2">
                    {t.messages.map((m) => (
                      <div key={m.id} className="text-sm">
                        <span className="text-xs text-breaker-mute">
                          Breaker support · {when(m.createdAt)}
                        </span>
                        <p className="text-breaker-ink-2 whitespace-pre-wrap">{m.body}</p>
                      </div>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function SupportPage() {
  return <FleetSupport />;
}
